// Filter der Aktivitäten-Listen in der Bedienleiste (Agent 5C): Mitarbeiter, Zeitraum, Richtung, Ergebnis (Anrufe), Lead.
// Der Zustand steht in der Adresse der Seite (teilbar), diese Komponente zeigt ihn nur und meldet Änderungen.
import type { AktivitaetenArt, Richtung } from "../api/aktivitaeten";
import type { NutzerKurz } from "../api/types";
import { Auswahl } from "../components/Auswahl";
import { Icon } from "../components/Icon";
import { KompaktWert } from "../kontakte/liste/KompaktWert";
import { ERGEBNISSE } from "./texte";
import { ZEITRAEUME, istZeitraum, zeitraumText, type Zeitraum } from "./zeiten";

export interface FilterStand {
  user: string | null;
  zeitraum: Zeitraum;
  eigenVon: string | null;
  eigenBis: string | null;
  richtung: Richtung | null;
  ergebnisse: string[];
}

interface Props {
  art: AktivitaetenArt;
  stand: FilterStand;
  nutzer: NutzerKurz[];
  /** Name des Leads, auf den die Liste eingeschränkt ist (Adresse `?lead=`); null = alle Leads */
  leadName: string | null;
  onAendern: (teil: Partial<FilterStand>) => void;
  onLeadEntfernen: () => void;
  onLeeren: () => void;
  /** irgendein Filter gesetzt (für „Filter leeren“) */
  aktiv: boolean;
}

const MIT_RICHTUNG: AktivitaetenArt[] = ["anrufe", "emails", "sms", "whatsapp"];

export function AktivitaetenFilter({ art, stand, nutzer, leadName, onAendern, onLeadEntfernen, onLeeren, aktiv }: Props) {
  const nutzerOptionen = nutzer.map((n) => ({ wert: n.close_user_id ?? n.id, label: n.name, unter: n.is_active ? null : "inaktiv" }));
  const zeitOptionen = ZEITRAEUME.filter((z) => z.id !== "alle").map((z) => ({ wert: z.id, label: z.label }));
  const richtungen: { wert: Richtung | null; label: string }[] = [
    { wert: null, label: "Alle" },
    { wert: "ausgehend", label: "Ausgehend" },
    { wert: "eingehend", label: "Eingehend" },
  ];
  return (
    <span className="akt-filter">
      <span className="akt-filter__nutzer">
        <Auswahl
          optionen={nutzerOptionen}
          wert={stand.user}
          leerErlaubt
          platzhalter="Alle Mitarbeiter"
          breite={240}
          titel="Nach Mitarbeiter filtern"
          onWahl={(w) => onAendern({ user: (w as string | null) || null })}
        />
      </span>
      <span className="akt-filter__zeit">
        <Auswahl
          optionen={zeitOptionen}
          wert={stand.zeitraum === "alle" ? null : stand.zeitraum}
          leerErlaubt
          platzhalter="Alle Zeiten"
          breite={200}
          titel="Nach Zeitraum filtern"
          knopfInhalt={stand.zeitraum === "eigen" ? <KompaktWert text={zeitraumText("eigen", stand.eigenVon, stand.eigenBis)} /> : undefined}
          onWahl={(w) => {
            const z = typeof w === "string" && istZeitraum(w) ? w : "alle";
            onAendern({ zeitraum: z, ...(z !== "eigen" ? { eigenVon: null, eigenBis: null } : {}) });
          }}
        />
      </span>
      {stand.zeitraum === "eigen" ? (
        <span className="akt-filter__daten">
          <input
            type="date"
            className="akt-filter__datum"
            aria-label="Von (Datum)"
            value={stand.eigenVon ?? ""}
            max={stand.eigenBis ?? undefined}
            onChange={(e) => onAendern({ eigenVon: e.target.value || null })}
          />
          <span className="leise">bis</span>
          <input
            type="date"
            className="akt-filter__datum"
            aria-label="Bis (Datum, einschließlich)"
            value={stand.eigenBis ?? ""}
            min={stand.eigenVon ?? undefined}
            onChange={(e) => onAendern({ eigenBis: e.target.value || null })}
          />
        </span>
      ) : null}
      {MIT_RICHTUNG.includes(art) ? (
        <span className="segment" role="group" aria-label="Richtung">
          {richtungen.map((r) => (
            <button
              key={r.label}
              type="button"
              className={`segment__knopf${stand.richtung === r.wert ? " segment__knopf--an" : ""}`}
              aria-pressed={stand.richtung === r.wert}
              onClick={() => onAendern({ richtung: r.wert })}
            >
              {r.label}
            </button>
          ))}
        </span>
      ) : null}
      {art === "anrufe" ? (
        <span className="akt-filter__ergebnis">
          <Auswahl
            optionen={ERGEBNISSE.map((e) => ({ wert: e.wert, label: e.label }))}
            wert={stand.ergebnisse}
            mehrfach
            leerErlaubt
            platzhalter="Alle Ergebnisse"
            breite={240}
            titel="Nach Anrufergebnis filtern"
            knopfInhalt={stand.ergebnisse.length > 1 ? <KompaktWert text={`${stand.ergebnisse.length} Ergebnisse`} /> : undefined}
            onWahl={(w) => onAendern({ ergebnisse: w as string[] })}
          />
        </span>
      ) : null}
      {leadName !== null ? (
        <span className="chip akt-filter__lead">
          Lead: {leadName}
          <button type="button" className="symbolknopf symbolknopf--klein" aria-label="Lead-Filter entfernen" onClick={onLeadEntfernen}>
            <Icon name="schliessen" groesse={12} />
          </button>
        </span>
      ) : null}
      {aktiv ? (
        <button type="button" className="knopf knopf--leise knopf--klein" onClick={onLeeren}>
          Filter leeren
        </button>
      ) : null}
    </span>
  );
}
